import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Collapse,
  Alert,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface ErrorFallbackProps {
  error: Error | null;
  resetErrorBoundary: () => void;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetErrorBoundary: _resetErrorBoundary }) => {
  const [showDetails, setShowDetails] = useState(false);

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 5 },
            width: "100%",
            textAlign: "center",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 80,
                color: "error.main",
                opacity: 0.8,
              }}
            />
          </Box>

          <Typography
            component="h1"
            variant="h4"
            gutterBottom
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            Oops! We updated our software while you were using it
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 400, mx: "auto" }}
          >
            We apologize for the inconvenience. Please try refreshing the page
            or return to the home page.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              sx={{ minWidth: 160 }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              sx={{ minWidth: 160 }}
            >
              Go Home
            </Button>
          </Box>

          {error && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowDetails(!showDetails)}
                endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: "text.secondary", textTransform: "none" }}
              >
                {showDetails ? "Hide" : "Show"} technical details
              </Button>
              <Collapse in={showDetails}>
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    textAlign: "left",
                    "& .MuiAlert-message": {
                      width: "100%",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      m: 0,
                    }}
                  >
                    {error.toString()}
                  </Typography>
                </Alert>
              </Collapse>
            </Box>
          )}
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3 }}
        >
          If this problem persists, please contact support.
        </Typography>
      </Box>
    </Container>
  );
};
